import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  archiveEmailTemplate,
  getEmailTemplate,
  previewEmailTemplate,
  searchCertificates,
  sendTestEmail,
  updateEmailTemplate,
  type Certificate,
  type EmailTemplate,
  type EmailTemplateUpdate,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { useConfirmFocus } from "@/components/confirmFocus";
import { Field, OtherProblems, inputClass, serverProblems, submitClass } from "@/components/form";
import { SearchBox } from "@/components/SearchBox";
import { DetailField, EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { MERGE_FIELDS } from "@/features/email/choices";

const detailApi = getRouteApi("/staff/email-templates/$templateId");

const PREVIEW_DELAY_MS = 400;
const RATE_LIMITED = "You can send 5 test emails a minute; try again shortly.";
const FIELDS = ["name", "subject", "body_text"] as const;

export function EmailTemplatePage() {
  const { templateId } = detailApi.useParams();
  const template = useQuery({ queryKey: ["email-template", templateId], queryFn: () => getEmailTemplate(templateId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (template.error instanceof ApiError && [404, 422].includes(template.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Email template not found" detail="No email template matches this link." />
        <Link to="/email-templates" className="text-sm font-medium text-zinc-950 underline">
          Back to email templates
        </Link>
      </section>
    );
  }
  if (!template.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Email template" detail={templateId} />
        <ErrorBanner error={template.error} />
        {template.isLoading ? <EmptyState title="Loading email template" detail="Fetching the template." /> : null}
      </section>
    );
  }
  return <TemplateView template={template.data} />;
}

function usage(template: EmailTemplate) {
  const used = template.is_default
    ? "Default template"
    : template.program_count > 0
      ? `Used by ${template.program_count} program${template.program_count === 1 ? "" : "s"}`
      : "Not used by any program";
  return template.archived_at ? `${used} · Archived` : used;
}

function TemplateView({ template }: { template: EmailTemplate }) {
  const queryClient = useQueryClient();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [defaulted, setDefaulted] = useState(false);
  const defaultedRef = useRef<HTMLParagraphElement>(null);
  const archived = Boolean(template.archived_at);
  // The Make default card unmounts on success; hand focus to its outcome instead of the body.
  useEffect(() => {
    if (defaulted && template.is_default) defaultedRef.current?.focus();
  }, [defaulted, template.is_default]);

  const store = async (next: EmailTemplate) => {
    // A refetch begun before the save would otherwise land after it with the old values.
    await queryClient.cancelQueries({ queryKey: ["email-template", template.id] });
    queryClient.setQueryData(["email-template", template.id], next);
    await queryClient.invalidateQueries({ queryKey: ["email-templates"] });
  };

  return (
    <section className="space-y-5">
      <WorkspaceHeader title={template.name} detail={usage(template)} />
      {archived ? (
        <>
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <dl className="grid gap-3 text-sm">
              <DetailField label="Subject" value={template.subject} />
              <DetailField label="Body" value={<span className="whitespace-pre-wrap">{template.body_text}</span>} />
            </dl>
          </div>
          <Preview
            templateId={template.id}
            subject={template.subject}
            body={template.body_text}
            certificate={certificate}
            onCertificate={setCertificate}
          />
        </>
      ) : (
        <EditTemplate template={template} certificate={certificate} onCertificate={setCertificate} onSaved={store} />
      )}
      {defaulted && template.is_default ? (
        <p ref={defaultedRef} role="status" tabIndex={-1} className="text-sm font-medium text-emerald-700 outline-none">
          This is now the default template.
        </p>
      ) : null}
      {archived || template.is_default ? null : (
        <>
          <MakeDefault
            template={template}
            onSaved={async (next) => {
              await store(next);
              setDefaulted(true);
            }}
          />
          <ArchiveTemplate template={template} />
        </>
      )}
    </section>
  );
}

function EditTemplate({
  template,
  certificate,
  onCertificate,
  onSaved,
}: {
  template: EmailTemplate;
  certificate: Certificate | null;
  onCertificate: (next: Certificate | null) => void;
  onSaved: (next: EmailTemplate) => Promise<void>;
}) {
  const [name, setName] = useState(template.name);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body_text);
  const [saved, setSaved] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const caret = useRef<number | null>(null);
  // A refetch with another editor's change replaces the form; our own save lands on the same values.
  const signature = JSON.stringify([template.name, template.subject, template.body_text]);
  const [seen, setSeen] = useState(signature);
  if (signature !== seen) {
    setSeen(signature);
    setName(template.name);
    setSubject(template.subject);
    setBody(template.body_text);
  }

  // Runs after the inserted text is in the textarea, so the caret lands after it.
  useLayoutEffect(() => {
    if (caret.current === null || !bodyRef.current) return;
    bodyRef.current.focus();
    bodyRef.current.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const update: EmailTemplateUpdate = {};
  if (name.trim() !== template.name) update.name = name.trim();
  if (subject.trim() !== template.subject) update.subject = subject.trim();
  if (body !== template.body_text) update.body_text = body;

  const save = useMutation({
    mutationFn: (changes: EmailTemplateUpdate) => updateEmailTemplate(template.id, changes),
    onSuccess: async (next) => {
      setSaved(true);
      setName(next.name);
      setSubject(next.subject);
      setBody(next.body_text);
      await onSaved(next);
    },
  });
  const edited = () => {
    setSaved(false);
    if (save.error) save.reset();
  };
  const server = serverProblems(save.error, FIELDS);
  const conflict = save.error instanceof ApiError && save.error.status === 409 ? save.error.message : undefined;
  const problems = {
    name: name.trim() === "" ? "is required" : (server.byField.name ?? conflict),
    subject: subject.trim() === "" ? "is required" : server.byField.subject,
    body_text: body.trim() === "" ? "is required" : server.byField.body_text,
  };
  const blocked = Object.values(problems).some(Boolean) || Object.keys(update).length === 0;

  const insert = (field: string) => {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + field + body.slice(end));
    caret.current = start + field.length;
    edited();
  };

  return (
    <>
      <form
        noValidate
        aria-label="Edit email template"
        className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!blocked && !save.isPending) save.mutate(update);
        }}
      >
        <h2 className="text-sm font-semibold">Wording</h2>
        <Field label="Name" problem={problems.name}>
          {(props) => (
            <input
              {...props}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Subject" problem={problems.subject}>
          {(props) => (
            <input
              {...props}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Body" problem={problems.body_text}>
          {(props) => (
            <textarea
              {...props}
              ref={bodyRef}
              rows={14}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-zinc-500">Insert into the body:</span>
          {MERGE_FIELDS.map((field) => (
            <button
              key={field}
              type="button"
              onClick={() => insert(field)}
              className="rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 font-mono text-zinc-700 hover:bg-zinc-100"
            >
              {field}
            </button>
          ))}
        </div>
        <ErrorBanner error={save.error instanceof ApiError && [409, 422].includes(save.error.status) ? null : save.error} />
        <OtherProblems lines={server.other} />
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={blocked || save.isPending} className={submitClass}>
            {save.isPending ? "Saving..." : "Save changes"}
          </button>
          {/* No API reports in-flight sends per template, so the warning is always shown. */}
          <p className="text-xs text-zinc-500">Saved changes apply to every email not yet sent, including sends in progress.</p>
        </div>
        {saved ? (
          <p role="status" className="text-sm font-medium text-emerald-700">
            Saved.
          </p>
        ) : null}
      </form>
      <Preview templateId={template.id} subject={subject} body={body} certificate={certificate} onCertificate={onCertificate} />
      <SendTest templateId={template.id} certificateId={certificate?.id} unsaved={Object.keys(update).length > 0} />
    </>
  );
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function Preview({
  templateId,
  subject,
  body,
  certificate,
  onCertificate,
}: {
  templateId: string;
  subject: string;
  body: string;
  certificate: Certificate | null;
  onCertificate: (next: Certificate | null) => void;
}) {
  const { can } = useSession();
  const [tab, setTab] = useState<"email" | "text">("email");
  // One string so a single timer covers every input; an object would restart it each render.
  const [dSubject, dBody, dCertificate] = JSON.parse(
    useDebounced(JSON.stringify([subject, body, certificate?.id ?? null]), PREVIEW_DELAY_MS),
  ) as [string, string, string | null];
  const preview = useQuery({
    queryKey: ["email-preview", templateId, dSubject, dBody, dCertificate],
    queryFn: () =>
      previewEmailTemplate(templateId, {
        subject: dSubject,
        body_text: dBody,
        ...(dCertificate ? { certificate_id: dCertificate } : {}),
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const invalid = preview.error instanceof ApiError && preview.error.status === 422 ? preview.error : null;
  // keepPreviousData would otherwise show the last good preview beside the error.
  const data = preview.error ? undefined : preview.data;

  return (
    <section aria-labelledby="email-preview" className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id="email-preview" className="text-sm font-semibold">
        Preview
      </h2>
      {can("certificate.read") ? (
        <CertificatePicker certificate={certificate} onCertificate={onCertificate} />
      ) : (
        <p className="text-sm text-zinc-500">Previews use sample values.</p>
      )}
      {invalid ? (
        <p className="text-sm text-red-700">
          Fix the template to see a preview: {invalid.details[0]?.issue ?? invalid.message}
        </p>
      ) : (
        <ErrorBanner error={preview.error} />
      )}
      {data ? (
        <>
          <p className="text-sm">
            <span className="text-zinc-500">Subject: </span>
            <span className="font-medium text-zinc-900">{data.subject}</span>
          </p>
          <div role="group" aria-label="Preview format" className="flex gap-2 border-b border-zinc-200">
            {(
              [
                ["email", "Email"],
                ["text", "Plain text"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={tab === key}
                onClick={() => setTab(key)}
                className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${tab === key ? "border-brand font-medium text-zinc-950" : "border-transparent text-zinc-500"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === "email" ? (
            // No allow-* tokens: the template's HTML runs no scripts and gets no origin.
            <iframe title="Email preview" sandbox="" srcDoc={data.html} className="h-96 w-full rounded-md border border-zinc-200 bg-white" />
          ) : (
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-zinc-50 p-3 text-sm text-zinc-800">{data.text}</pre>
          )}
        </>
      ) : preview.isLoading ? (
        <p className="text-sm text-zinc-500">Loading preview...</p>
      ) : null}
    </section>
  );
}

function CertificatePicker({
  certificate,
  onCertificate,
}: {
  certificate: Certificate | null;
  onCertificate: (next: Certificate | null) => void;
}) {
  const [q, setQ] = useState("");
  const results = useQuery({
    queryKey: ["certificates", { q, limit: 5 }],
    queryFn: () => searchCertificates({ q, limit: 5 }),
    enabled: q !== "",
  });
  const items = results.data?.items ?? [];
  const label = (c: Certificate) => `${c.doc_control_id} — ${c.member_full_name}`;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox q={q} onSearch={setQ} ariaLabel="Preview with a certificate" placeholder="Preview with a certificate" />
        {certificate ? (
          <ToolbarButton onClick={() => onCertificate(null)}>Use sample values</ToolbarButton>
        ) : null}
      </div>
      <p className="text-zinc-500">{certificate ? `Previewing with ${label(certificate)}.` : "Previewing with sample values."}</p>
      <ErrorBanner error={results.error} />
      {q !== "" && results.isSuccess && items.length === 0 ? <p className="text-zinc-500">No certificates match.</p> : null}
      {items.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onCertificate(c)}
                aria-pressed={certificate?.id === c.id}
                className="rounded-md border border-zinc-300 px-2 py-1 text-left hover:bg-zinc-50 aria-pressed:border-brand"
              >
                {label(c)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SendTest({ templateId, certificateId, unsaved }: { templateId: string; certificateId?: string; unsaved: boolean }) {
  // isPending lands a render late; the ref stops a double click sending twice.
  const submitting = useRef(false);
  const send = useMutation({
    mutationFn: () => sendTestEmail(templateId, certificateId ? { certificate_id: certificateId } : undefined),
    onSettled: () => {
      submitting.current = false;
    },
  });
  const { reset } = send;
  // A result for another certificate would mislead.
  const lastCertificate = useRef(certificateId);
  useEffect(() => {
    if (lastCertificate.current === certificateId) return;
    lastCertificate.current = certificateId;
    reset();
  }, [certificateId, reset]);
  const limited = send.error instanceof ApiError && send.error.status === 429;

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Test email</h2>
          <p className="mt-1 text-xs text-zinc-500">Sends the saved wording to your own address.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {unsaved ? <p className="text-xs text-zinc-500">Save first to test your changes.</p> : null}
          <ToolbarButton
            disabled={send.isPending}
            onClick={() => {
              if (submitting.current) return;
              submitting.current = true;
              send.mutate();
            }}
          >
            {send.isPending ? "Sending..." : "Send test to me"}
          </ToolbarButton>
        </div>
      </div>
      {send.isSuccess ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Sent to {send.data.sent_to}.
        </p>
      ) : null}
      {limited ? <p className="text-sm text-red-700">{RATE_LIMITED}</p> : <ErrorBanner error={send.error} />}
    </div>
  );
}

function MakeDefault({ template, onSaved }: { template: EmailTemplate; onSaved: (next: EmailTemplate) => Promise<void> }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);
  const submitting = useRef(false);
  const save = useMutation({
    mutationFn: () => updateEmailTemplate(template.id, { is_default: true }),
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: async (next) => {
      setConfirming(false);
      await onSaved(next);
      // The previous default's page would still say it is the default.
      await queryClient.invalidateQueries({ queryKey: ["email-template"], predicate: (q) => q.queryKey[1] !== template.id });
    },
    onError: () => setConfirming(false),
  });

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Default</h2>
          <p className="mt-1 text-xs text-zinc-500">Programs without their own template use the default.</p>
        </div>
        <ToolbarButton ref={trigger} disabled={confirming || save.isPending} onClick={() => setConfirming(true)}>
          Make default
        </ToolbarButton>
      </div>
      {confirming ? (
        <div role="group" aria-label="Confirm make default" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">Programs without their own template will use {template.name}.</p>
          <ToolbarButton ref={confirm} disabled={save.isPending} onClick={() => {
              if (submitting.current) return;
              submitting.current = true;
              save.mutate();
            }}
          >
            {save.isPending ? "Saving..." : `Make ${template.name} the default`}
          </ToolbarButton>
          <ToolbarButton disabled={save.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ErrorBanner error={save.error} />
    </div>
  );
}

function ArchiveTemplate({ template }: { template: EmailTemplate }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);
  const submitting = useRef(false);
  const archive = useMutation({
    mutationFn: () => archiveEmailTemplate(template.id),
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: async (next) => {
      // Leave first: the page would otherwise swap to the read-only view mid-navigation.
      await navigate({ to: "/email-templates" });
      queryClient.setQueryData(["email-template", template.id], next);
      await queryClient.invalidateQueries({ queryKey: ["email-templates"] });
    },
    onError: () => setConfirming(false),
  });

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Archive</h2>
          <p className="mt-1 text-xs text-zinc-500">Only a template no program uses can be archived.</p>
        </div>
        <ToolbarButton ref={trigger} tone="danger" disabled={confirming || archive.isPending} onClick={() => setConfirming(true)}>
          Archive
        </ToolbarButton>
      </div>
      {confirming ? (
        <div role="group" aria-label="Confirm archive" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">Archive {template.name}? It can no longer be chosen for a program.</p>
          <ToolbarButton ref={confirm} tone="danger" disabled={archive.isPending} onClick={() => {
              if (submitting.current) return;
              submitting.current = true;
              archive.mutate();
            }}
          >
            {archive.isPending ? "Archiving..." : `Archive ${template.name}`}
          </ToolbarButton>
          <ToolbarButton disabled={archive.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ErrorBanner error={archive.error} />
    </div>
  );
}
